/**
 * One article, in the frame the settings pages use: the navigation on the left and
 * one column that scrolls, holding the body, previous/next, and a table of contents
 * built from the blocks that carry a heading. The breadcrumb sits in the app header
 * (HelpdeskBreadcrumb), as it does in Settings.
 */

import { Alert } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible"
import { Typography } from "@/components/ui/typography"
import { useFeatureFlag } from "@/contexts/FeatureFlagContext"
import {
  AskAgentAboutHelpArticle,
  useHelpArticleAgentContext,
} from "@/features/agent-chat/components/AskAgentAboutHelpArticle"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { cn } from "@/lib/utils"
import {
  ArrowLeft,
  ArrowRight,
  ChevronDown,
  Clock,
  Info,
  Pencil,
} from "lucide-react"
import { useMotionValue } from "motion/react"
import * as m from "motion/react-m"
import { useEffect, useMemo, useRef, useState, type RefObject } from "react"
import { Link, useSearchParams } from "react-router"
import type { HelpContent } from "../api/types"
import { adjacentArticles, HELP_LANGS, type ArticleLocation } from "../content"
import { PAGE_ENTER, TRAVEL } from "../motion"
import { helpArticlePath } from "../routes"
import { ArticleBlocks, blockHeadings } from "./ArticleBlocks"
import { ArticleFeedback } from "./ArticleFeedback"
import { HelpdeskArticleEditor } from "./HelpdeskArticleEditor"
import { HelpdeskArticleSidebar } from "./HelpdeskArticleSidebar"

/** Edit mode lives in the URL, so it survives a reload and can be linked. */
const EDIT_PARAM = "edit"

type Heading = { anchor: string; label: string }

/**
 * Runs `update` now and whenever the article column scrolls, resizes, or its
 * content grows, as when an entry folds open. Returns the cleanup.
 */
function watchColumn(root: HTMLElement, update: () => void): () => void {
  update()
  root.addEventListener("scroll", update, { passive: true })
  const observer =
    typeof ResizeObserver === "undefined" ? null : new ResizeObserver(update)
  observer?.observe(root)
  if (root.lastElementChild) observer?.observe(root.lastElementChild)
  return () => {
    root.removeEventListener("scroll", update)
    observer?.disconnect()
  }
}

/**
 * The section currently being read: the last heading that has scrolled past the
 * top fifth of the article column. Observed on the column itself, which is the
 * scroller, not the window.
 */
function useActiveHeading(
  headings: Heading[],
  scrollerRef: RefObject<HTMLDivElement | null>,
  /** The editor replaces the column, so leaving it means a new one to observe. */
  isEditing: boolean,
): string | null {
  const [active, setActive] = useState<string | null>(
    headings[0]?.anchor ?? null,
  )

  useEffect(() => {
    const root = scrollerRef.current
    if (!root || headings.length === 0) return
    const update = () => {
      const limit = root.getBoundingClientRect().top + root.clientHeight * 0.2
      let current = headings[0].anchor
      for (const { anchor } of headings) {
        const element = document.getElementById(anchor)
        if (element && element.getBoundingClientRect().top <= limit)
          current = anchor
      }
      // A short last section never reaches the line, so the end of the column
      // stands in for it. scrollTop keeps a page that does not scroll on its first.
      const atEnd =
        root.scrollTop > 0 &&
        root.scrollTop + root.clientHeight >= root.scrollHeight - 1
      setActive(atEnd ? headings[headings.length - 1].anchor : current)
    }
    return watchColumn(root, update)
  }, [headings, scrollerRef, isEditing])

  return active
}

/** How far through the article the reader is, as a line under the app header. */
function ReadingProgress({
  scrollerRef,
}: {
  scrollerRef: RefObject<HTMLDivElement | null>
}) {
  const progress = useMotionValue(0)

  // Measured here rather than with useScroll, which calls a page with nothing to
  // scroll 100% read, and misses the page growing when an entry folds open.
  useEffect(() => {
    const root = scrollerRef.current
    if (!root) return
    const update = () => {
      const range = root.scrollHeight - root.clientHeight
      progress.set(range > 0 ? root.scrollTop / range : 0)
    }
    return watchColumn(root, update)
  }, [progress, scrollerRef])

  return (
    <m.div
      aria-hidden="true"
      style={{ scaleX: progress }}
      className="sticky top-0 z-10 -mb-0.5 h-0.5 origin-left bg-primary"
    />
  )
}

function TocLinks({
  headings,
  active,
  markerId,
}: {
  headings: Heading[]
  active: string | null
  /** Separate per list, so the two tables of contents do not share a marker. */
  markerId: string
}) {
  return (
    <ul className="flex flex-col border-l">
      {headings.map((heading) => {
        const isActive = heading.anchor === active
        return (
          <li key={heading.anchor} className="relative">
            {isActive ? (
              <m.span
                layoutId={markerId}
                transition={TRAVEL}
                aria-hidden="true"
                className="absolute inset-y-0 -left-px w-px bg-primary"
              />
            ) : null}
            <a
              href={`#${heading.anchor}`}
              aria-current={isActive ? "location" : undefined}
              className={cn(
                "block py-1.5 pl-3 text-sm leading-snug transition-colors duration-150",
                "focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none",
                isActive
                  ? "font-medium text-primary"
                  : "text-muted-foreground hover:text-white",
              )}
            >
              {heading.label}
            </a>
          </li>
        )
      })}
    </ul>
  )
}

/** Inside the scrolling column, so it stays beside the text it belongs to. */
function TableOfContents({
  headings,
  active,
  label,
}: {
  headings: Heading[]
  active: string | null
  label: string
}) {
  if (headings.length === 0) return null
  return (
    <nav
      aria-label={label}
      className="sticky top-6 hidden w-48 shrink-0 flex-col gap-2 self-start md:top-10 @6xl:flex"
    >
      <Typography as="muted">{label}</Typography>
      <TocLinks headings={headings} active={active} markerId="helpdesk-toc" />
    </nav>
  )
}

/** Where the side column does not fit, the contents fold in under the title. */
function InlineTableOfContents({
  headings,
  active,
  label,
}: {
  headings: Heading[]
  active: string | null
  label: string
}) {
  if (headings.length === 0) return null
  return (
    <Collapsible className="mb-6 rounded-lg border @6xl:hidden">
      <CollapsibleTrigger className="group flex w-full items-center justify-between rounded-lg px-4 py-3 text-left focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none">
        <Typography as="muted">{label}</Typography>
        <ChevronDown
          aria-hidden="true"
          className="size-4 text-muted-foreground transition-transform duration-200 group-data-[state=open]:rotate-180 motion-reduce:transition-none"
        />
      </CollapsibleTrigger>
      <CollapsibleContent className="overflow-hidden data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down motion-reduce:animate-none">
        <div className="px-4 pb-3">
          <TocLinks
            headings={headings}
            active={active}
            markerId="helpdesk-toc-inline"
          />
        </div>
      </CollapsibleContent>
    </Collapsible>
  )
}

function AdjacentLink({
  entry,
  direction,
  label,
}: {
  entry: ArticleLocation
  direction: "previous" | "next"
  label: string
}) {
  const isNext = direction === "next"
  return (
    <Link
      to={helpArticlePath(entry.category.id, entry.article.id)}
      className={cn(
        "flex flex-col gap-1 rounded-lg border px-4 py-3 transition-colors duration-150 hover:border-grey5",
        "focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none",
        isNext && "items-end text-right",
      )}
    >
      <span className="flex items-center gap-1 text-xs text-muted-foreground">
        {isNext ? null : <ArrowLeft aria-hidden="true" className="size-3" />}
        {label}
        {isNext ? <ArrowRight aria-hidden="true" className="size-3" /> : null}
      </span>
      <span className="text-sm font-medium text-white">
        {entry.article.title}
      </span>
    </Link>
  )
}

interface HelpdeskArticlePageProps {
  content: HelpContent
  location: ArticleLocation
}

export function HelpdeskArticlePage({
  content,
  location,
}: HelpdeskArticlePageProps) {
  const { translate } = useTranslation()
  const [searchParams, setSearchParams] = useSearchParams()
  const canEdit = useFeatureFlag("HELPDESK_EDIT")
  const { category, article } = location
  const { previous, next } = adjacentArticles(content, article.id)
  const isEditing = canEdit && searchParams.get(EDIT_PARAM) === "1"
  // Starts on the language actually being served, which is the page in front of
  // them; the switcher is how the other one gets written.
  const [editingLang, setEditingLang] = useState(article.lang)
  const scrollerRef = useRef<HTMLDivElement>(null)

  const setEditing = (editing: boolean) =>
    setSearchParams(
      (params) => {
        if (editing) params.set(EDIT_PARAM, "1")
        else params.delete(EDIT_PARAM)
        return params
      },
      // Entering is a navigation, so the back button leaves the editor again.
      // Leaving replaces that entry instead of stacking a third.
      { replace: !editing },
    )
  const headings = useMemo(
    () => blockHeadings(article.blocks, translate),
    [article.blocks, translate],
  )
  const activeHeading = useActiveHeading(headings, scrollerRef, isEditing)
  const isFallbackLanguage = article.lang !== content.lang
  const tocLabel = translate("HELPDESK/ON_THIS_PAGE")

  // A new article starts at its top, not at the previous one's scroll position.
  useEffect(() => {
    if (scrollerRef.current) scrollerRef.current.scrollTop = 0
  }, [article.id])

  // Registered for the page, not for the button: the agent should know which article is
  // open the moment someone opens the chat panel from the app chrome, whether or not
  // they ever press "ask about this".
  useHelpArticleAgentContext(article.id, article.route)

  if (isEditing) {
    return (
      <HelpdeskArticleEditor
        key={`${article.id}-${editingLang}`}
        articleId={article.id}
        articleTitle={article.title}
        lang={editingLang}
        languages={HELP_LANGS}
        onLangChange={setEditingLang}
        onClose={() => setEditing(false)}
      />
    )
  }

  return (
    // A container, not the window: asking the agent about this page docks the chat
    // beside it, and the side columns must give way to the text when that happens.
    <div className="@container flex h-full min-h-0 min-w-0">
      {/* No key: remounting on every article is what made the tree collapse
          and reopen on each click. */}
      <HelpdeskArticleSidebar
        content={content}
        activeArticleId={article.id}
        activeCategoryId={category.id}
      />

      <div ref={scrollerRef} className="min-w-0 flex-1 overflow-y-auto">
        <ReadingProgress scrollerRef={scrollerRef} />
        <div className="flex justify-center gap-10 px-4 py-6 md:px-10 md:py-10">
          <m.article
            key={article.id}
            {...PAGE_ENTER}
            className="w-full max-w-3xl min-w-0"
          >
            <div className="mb-6 flex flex-col gap-3">
              <Typography as="h3" role="heading" aria-level={1}>
                {article.title}
              </Typography>
              <div className="flex flex-wrap items-center gap-3">
                <span className="flex items-center gap-1.5 text-sm text-muted-foreground">
                  <Clock aria-hidden="true" className="size-3.5" />
                  {translate("HELPDESK/READING_TIME", {
                    minutes: article.minutes,
                  })}
                </span>
                {/* Renders nothing unless the sidechat, the agent tools, the helpdesk
                    flag and chat.use all line up, so a reader who cannot use the
                    assistant is never offered it. */}
                <AskAgentAboutHelpArticle
                  articleId={article.id}
                  title={article.title}
                />
                {canEdit ? (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setEditing(true)}
                  >
                    <Pencil aria-hidden="true" />
                    {translate("HELPDESK/EDIT")}
                  </Button>
                ) : null}
              </div>
            </div>

            {isFallbackLanguage ? (
              <Alert role="note" className="mb-6 border-0 bg-accent">
                <Info className="size-4" />
                <Typography as="muted">
                  {translate("HELPDESK/ONLY_IN_GERMAN")}
                </Typography>
              </Alert>
            ) : null}

            <InlineTableOfContents
              headings={headings}
              active={activeHeading}
              label={tocLabel}
            />

            <ArticleBlocks blocks={article.blocks} />

            <ArticleFeedback
              key={article.id}
              articleId={article.id}
              categoryId={category.id}
              lang={article.lang}
            />

            <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
              {previous ? (
                <AdjacentLink
                  entry={previous}
                  direction="previous"
                  label={translate("HELPDESK/PREVIOUS")}
                />
              ) : (
                <div className="hidden sm:block" />
              )}
              {next ? (
                <AdjacentLink
                  entry={next}
                  direction="next"
                  label={translate("HELPDESK/NEXT")}
                />
              ) : null}
            </div>
          </m.article>

          <TableOfContents
            headings={headings}
            active={activeHeading}
            label={tocLabel}
          />
        </div>
      </div>
    </div>
  )
}
